import React, { useState } from "react";

import classNames from "classnames";
import { keyBy, sum } from "lodash";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom-v5-compat";

import { DietaryEntities } from "Helpers/DataDefinitions/Dietaries";

import { Price, PrimaryAction, TitleBar, useSubmitButton } from "../Atoms";
import { Button } from "../Atoms";
import { Dietary } from "../Atoms";
import { Session } from "../Authentication";
import { Cart, CartItem, ItemMod as CartItemMod } from "../Cart/schema";
import { ItemCountdown } from "../Sections/Section";
import { useSession, useSessionDispatch } from "../Session/SessionContext";
import {
  getCanAddToCart,
  getIsWishlist,
  isRunningTab,
} from "../Session/sessionSelectors";
import { useVenue } from "../VenueContext";
import { useFetchLiveItemCountdown } from "../queries";
import type { Menu, Item, ItemMod, ItemModGroup, Venue } from "../schema";
import {
  CartItemModAttributesRequest,
  CartItemRequest,
  createCartItem,
  deleteCartItem,
  updateCartItem,
} from "./cartItemRequests";

type ItemModFormData = {
  modGroupUuid: string;
  itemModUuid: string;
  cartItemUuid?: string;
};

type OptionalModFormData = false | string | string[];

interface ItemFormData {
  modGroups?: {
    mandatory: Record<string, ItemModFormData>;
    // React hook form has a bug where it doesn't handle arrays of arrays correctly
    // It supports 3 different case each which return a different type this is super annoying
    // 1 optional mod but non selected returns false
    // 1 optional mod selected returns string ( the mod uuid)
    // Multiple optional mods returns string[] always
    optional: Record<string, OptionalModFormData>;
  };

  // Because of how react-hook-form handles className="hidden || invisible"  inputs, we need to use a <input type="hidden" />
  // Which produces a string value, so for full type safety we need to use a string here
  multiplier: string | number;
}

const parseOptionalMods = (optionalMods?: OptionalModFormData) => {
  if (!optionalMods) return [];
  if (typeof optionalMods === "string") return [optionalMods];
  return optionalMods;
};
const getDisableOrdering = (session: Session, venue: Venue) => {
  return isRunningTab(session) && !venue.enabledFeatures.tablePayOrdering;
};
const isFavouriteMod = (mod: ItemMod) => mod.favourite;
const isMandatory = (mod: CartItemMod) => mod.modType == "mandatory";
const isOptional = (mod: CartItemMod) => mod.modType == "optional";
const isMandatoryModGroup = (mod: ItemModGroup) =>
  mod.modGroupType == "mandatory";
const isOptionalModGroup = (modGroup: ItemModGroup) =>
  !isMandatoryModGroup(modGroup);
const isEmptyModGroup = (group: ItemModGroup) => group.itemMods.length > 0;

const itemModToFormData = (itemMod: ItemMod): ItemModFormData => ({
  modGroupUuid: itemMod.uuid,
  itemModUuid: itemMod.uuid,
  cartItemUuid: undefined,
});

const cartItemModToFormData = (cartItemMod: CartItemMod): ItemModFormData => ({
  modGroupUuid: cartItemMod.itemModGroupId,
  itemModUuid: cartItemMod.itemModId,
  cartItemUuid: cartItemMod.id,
});
const generateDefaultFormData = (
  item: Item,
  cartItem?: CartItem,
): ItemFormData => {
  const defaultModGroups: Record<string, ItemModFormData> = item.itemModGroups
    .filter(isMandatoryModGroup)
    .filter(isEmptyModGroup)
    .reduce(
      (acc, modGroup): Record<string, ItemModFormData> => ({
        ...acc,
        [modGroup.uuid]: modGroup.itemMods
          .filter(isFavouriteMod)
          .map(itemModToFormData)[0],
      }),
      {} as Record<string, ItemModFormData>,
    );
  const formData: ItemFormData = {
    modGroups: {
      optional: cartItem
        ? cartItem.itemMods.filter(isOptional).reduce(
            (acc, cartItem) => ({
              [cartItem.itemModGroupId]: [
                ...(acc[cartItem.itemModGroupId] || []),
                cartItem.itemModId,
              ],
            }),
            {} as Record<string, string[]>,
          )
        : {},
      mandatory: (cartItem?.itemMods || []).filter(isMandatory).reduce(
        (acc, cartItemMod) => ({
          ...acc,
          [cartItemMod.itemModGroupId]: cartItemModToFormData(cartItemMod),
        }),
        defaultModGroups,
      ),
    },
    multiplier: cartItem?.multiplier || 1,
  };
  return formData;
};
const getItemModsByUuid = (item: Item) =>
  keyBy(
    item.itemModGroups.flatMap((modGroup) => modGroup.itemMods),
    "uuid",
  );
const generateCartItem =
  (cart: Cart, cartItem?: CartItem) =>
  (item: Item, result: Partial<ItemFormData>): CartItemRequest => {
    const itemMods = getItemModsByUuid(item);
    const selectedMandatoryMods: CartItemModAttributesRequest[] =
      Object.entries(result.modGroups?.mandatory || {}).map(
        ([modGroupUuid, itemModFormData]) => ({
          modType: "mandatory",
          itemModId: itemModFormData.itemModUuid,
          itemModGroupId: modGroupUuid,
          salesPriceId: itemMods[itemModFormData.itemModUuid].salesPriceUuid,
          id: itemModFormData.cartItemUuid,
        }),
      );

    const selectedOptionalMods: CartItemModAttributesRequest[] =
      item.itemModGroups.filter(isOptionalModGroup).flatMap((modGroup) =>
        modGroup.itemMods
          .map(
            (itemMod): CartItemModAttributesRequest => ({
              modType: "optional",
              itemModId: itemMod.uuid,
              itemModGroupId: modGroup.uuid,
              salesPriceId: itemMod.salesPriceUuid,
              id: cartItem?.itemMods.find(
                (cartItemMod) => cartItemMod.itemModId == itemMod.uuid,
              )?.id,
              delete: !parseOptionalMods(
                result.modGroups?.optional[modGroup.uuid],
              ).some((itemModFormData) => itemModFormData == itemMod.uuid),
            }),
          )
          .filter((itemMod) => !(itemMod.delete && !itemMod.id)),
      );

    const missingCartData: CartItemRequest = {
      cartId: cart.uuid,
      itemId: item.uuid,
      multiplier: +(result?.multiplier || 1),
      itemModsAttributes: [...selectedOptionalMods, ...selectedMandatoryMods],
      salesPriceId: item.salesPriceUuid,
    };

    return missingCartData;
  };

const previewPrice = (item: Item, formData: ItemFormData): number => {
  const itemMods = getItemModsByUuid(item);

  const mandatoryMods = formData.modGroups?.mandatory || {};
  const optionalMods = formData.modGroups?.optional || {};
  const mandatoryPrice = Object.values(mandatoryMods)
    .filter((i) => i)
    .map(
      (itemModFormData) =>
        itemMods[itemModFormData.itemModUuid]?.salesPrice || 0,
    );
  const optionalModPrice = Object.values(optionalMods).flatMap((itemModUuids) =>
    parseOptionalMods(itemModUuids).map(
      (itemModUuid) => itemMods[itemModUuid]?.salesPrice || 0,
    ),
  );
  return (
    sum([item.salesPrice, ...mandatoryPrice, ...optionalModPrice]) *
    +formData.multiplier
  );
};
const CartItemForm = ({
  cartItem,
  defaultFormData,
  item,
  itemCount,
  onCompletion,
  isEditing,
  onDeletion,
}: {
  menu: Menu;
  cartItem?: CartItem;
  defaultFormData: ItemFormData;
  item: Item;
  itemCount?: number;
  isEditing?: boolean;
  onCompletion: (result: CartItemRequest) => Promise<void>;
  onDeletion?: () => Promise<void>;
}) => {
  const { t } = useTranslation();
  const { cart } = useSession();
  const session = useSession();
  const venue = useVenue();
  const nonEmptyModGroups = item.itemModGroups
    .filter(isEmptyModGroup)
    .map((modGroup) => ({
      ...modGroup,
      favouriteItemMod:
        modGroup.itemMods.find(isFavouriteMod) || modGroup.itemMods[0],
    }));

  const { register, watch, handleSubmit, ...formProps } = useForm<ItemFormData>(
    { defaultValues: defaultFormData },
  );
  const onSubmit = async (result: ItemFormData) => {
    const cartItemRequest = generateCartItem(cart, cartItem)(item, result);
    setRecentlyAdded(true);
    setTimeout(() => {
      setRecentlyAdded(false);
    }, 2000);
    await onCompletion(cartItemRequest);
  };

  const price = previewPrice(item, watch());
  const incrementMultiplier = (value: number) => () => {
    const newValue: number = Math.max(
      1,
      +formProps.getValues("multiplier") + value,
    );
    formProps.setValue("multiplier", newValue);
  };

  const [recentlyAdded, setRecentlyAdded] = useState(false);
  const [submissionState, trackSubmit] = useSubmitButton();
  const [deleteSubmissionState, trackDelete] = useSubmitButton();
  const showRightSide = nonEmptyModGroups.length > 0;
  const canAddToCart = getCanAddToCart(session, venue);
  const isWishList = getIsWishlist(session, venue);
  const editingActionContent = isWishList
    ? t("items.updateWishList")
    : t("items.updateOrderItem");
  const wishListActionContent = recentlyAdded ? (
    t("items.addedToWishlist")
  ) : (
    <>
      {t("items.addToWishlist")} (<Price showCurrency value={price} />)
    </>
  );
  const orderActionContent = recentlyAdded ? (
    t("items.addedToOrder")
  ) : (
    <>
      {t("items.addToOrder")}{" "}
      <span className="inline-block ">
        <Price showCurrency value={price} />
      </span>
    </>
  );

  const primaryActionContent = isWishList
    ? wishListActionContent
    : orderActionContent;

  const isOrderingDisabled = getDisableOrdering(session, venue);
  const primaryActions = !isOrderingDisabled ? (
    <div className="flex justify-between items-center">
      <div className="flex flex-1 gap-8">
        <div className="flex-1">
          <MultiplierIncrementer
            multiplier={+watch("multiplier")}
            maxMultiplier={itemCount}
            incrementMultiplier={incrementMultiplier(1)}
            decrementMultiplier={incrementMultiplier(-1)}
          />
        </div>
        {isEditing && onDeletion && (
          <div className="flex-1">
            <Button
              alert
              type="button"
              submissionState={deleteSubmissionState}
              onClick={trackDelete(onDeletion)}
            >
              {t("items.removeFromOrder")}
            </Button>
          </div>
        )}

        <div className="flex-1">
          <Button primary submissionState={submissionState}>
            <div className="whitespace-nowrap">
              {isEditing ? editingActionContent : primaryActionContent}
            </div>
          </Button>
        </div>
      </div>
    </div>
  ) : (
    <div>
      <div className="text-center">{t("items.orderingDisabled")}</div>
      <Button primary> {t("items.returnToOrder")} </Button>
    </div>
  );
  return (
    <div>
      <form onSubmit={handleSubmit(trackSubmit(onSubmit))}>
        <input type="hidden" {...register("multiplier")} />
        <div className="md:relative md:border-[6px] md:border-venue-tertiary md:rounded-venue-brand md:px-16 md:py-16 md:space-y-32">
          <TitleBar alwaysShowBackButton>
            <div className="flex justify-center items-center gap-12">
              <div className="text-3xl font-bold">{item.name}</div>
              <div className="my-auto">
                <ItemCountdown count={itemCount} />
              </div>
            </div>
          </TitleBar>
          <div className="space-y-48">
            <div className="md:gap-32 md:flex space-y-32 md:space-y-0 md:justify-center">
              <div className="flex-1 md:flex-none md:w-1/2 space-y-24 ">
                <div>
                  {
                    item.heroImageUrl ? (
                      <div className="-mx-16 md:mx-0 aspect-w-3 aspect-h-2 ">
                        <img
                          src={item.heroImageUrl}
                          className="object-cover shadow-venue-brand-style md:rounded-venue-brand"
                        />
                      </div>
                    ) : null
                    // This still needs to be sussed if using or not
                    // <div className="bg-venue-primary text-center text-3xl py-16 text-venue-secondary">
                    //   {item.name}
                    // </div>
                  }
                </div>
                <div className="space-y-16">
                  <div className="flex gap-12 flex-wrap">
                    {item.dietaries
                      .map((dietary) => DietaryEntities[dietary])
                      .map((dietary) => (
                        <Dietary dietary={dietary} key={dietary.value} />
                      ))}
                  </div>
                  <div className="md:hidden text-2xl font-bold">
                    <Price value={price} />
                  </div>
                  <div className="text-base font-normal">
                    {item.marketingBlurb}
                  </div>
                  <div className="text-base font-normal">{item.detail}</div>
                </div>
              </div>
              {showRightSide && (
                <div className="md:flex-1 space-y-32 ">
                  {nonEmptyModGroups.map((modGroup) => (
                    <div key={modGroup.uuid} className="space-y-12">
                      <div>
                        <h2 className="text-2xl font-bold ">{modGroup.name}</h2>
                        <div className="text-base font-semibold italic">
                          {t(`items.modGroupType.${modGroup.modGroupType}`)}
                        </div>
                      </div>
                      <div className="space-y-12">
                        {modGroup.itemMods.map((mod) => (
                          <label
                            key={mod.uuid}
                            className="bg-venue-tertiary shadow-venue-brand-style rounded-venue-brand p-8 flex gap-8 items-center cursor-pointer font-medium text-base"
                          >
                            <div className="flex-none ">
                              {isMandatoryModGroup(modGroup) ? (
                                <>
                                  <input
                                    type="hidden"
                                    {...register(
                                      `modGroups.mandatory.${modGroup.uuid}.itemModGroupId`,
                                    )}
                                  />
                                  <input
                                    type="hidden"
                                    {...register(
                                      `modGroups.mandatory.${modGroup.uuid}.cartItemUuid`,
                                    )}
                                  />
                                  <input
                                    type={"radio"}
                                    id={`modGroups[mandatory][${modGroup.uuid}].itemModUuid`}
                                    {...register(
                                      `modGroups.mandatory.${modGroup.uuid}.itemModUuid`,
                                    )}
                                    value={mod.uuid}
                                    defaultChecked={
                                      modGroup.favouriteItemMod.uuid == mod.uuid
                                    }
                                  />
                                </>
                              ) : (
                                <input
                                  type={"checkbox"}
                                  id={`modGroups[optional][${modGroup.uuid}]`}
                                  {...register(
                                    `modGroups.optional.${modGroup.uuid}`,
                                  )}
                                  value={mod.uuid}
                                />
                              )}
                            </div>

                            <div className="flex-1">{mod.name}</div>
                            <div className="flex-none">
                              <Price value={mod.salesPrice} />
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="hidden md:block bg-venue-secondary md:-mx-16 px-16 md:sticky bottom-0 py-8">
              <div className="flex-1 md:flex gap-12 justify-between items-center">
                <div className="text-3xl font-bold flex-1">
                  <Price value={price} />
                </div>

                {itemCount == undefined || (itemCount && itemCount > 0) ? (
                  canAddToCart ? (
                    <>
                      <div className="flex-1 ">
                        <MultiplierIncrementer
                          maxMultiplier={itemCount}
                          multiplier={+watch("multiplier")}
                          incrementMultiplier={incrementMultiplier(1)}
                          decrementMultiplier={incrementMultiplier(-1)}
                        />
                      </div>

                      {isEditing && onDeletion && (
                        <div className="flex-1">
                          <Button
                            alert
                            type="button"
                            submissionState={deleteSubmissionState}
                            onClick={trackDelete(onDeletion)}
                          >
                            {t("items.removeFromOrder")}
                          </Button>
                        </div>
                      )}

                      <div className="flex-1">
                        <Button primary submissionState={submissionState}>
                          {isEditing
                            ? editingActionContent
                            : primaryActionContent}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="font-bold text-xl">
                      {t("items.orderingDisabled")}
                    </div>
                  )
                ) : (
                  <div className="flex-1">
                    <Button primary disabled submissionState={submissionState}>
                      {t("items.soldOut")}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <PrimaryAction>{primaryActions}</PrimaryAction>
        </div>
      </form>
    </div>
  );
};

export const EditCartItem = ({ menu }: { menu: Menu }) => {
  const { cartItemId } = useParams();
  const { t } = useTranslation();
  const { cart } = useSession();
  const navigate = useNavigate();
  const dispatch = useSessionDispatch();

  const cartItem = cart.items.find((i) => i.id == cartItemId);
  if (!cartItem) throw new Error(t("items.cartItemNotFound"));

  const itemUuid = cartItem.itemId;
  const item = menu.sections
    .flatMap((s) => s.items)
    .find((i) => i.uuid == itemUuid);
  if (!item) throw new Error(t("items.menuItemNotFound"));

  const onEditOrderItem = async (cartItem: CartItemRequest) => {
    if (!cartItemId) {
      throw new Error("No Cart Item Id");
    }
    const response = await updateCartItem({ id: cartItemId, ...cartItem });
    dispatch({ action: "updateCartItem", payload: response });
    navigate("/your-order/pending");
  };
  const removeOrderItem = async () => {
    const response = await deleteCartItem(cart.uuid, cartItem.id);
    dispatch({
      action: "removeCartItem",
      payload: { cartItemId: cartItem.id },
    });
    navigate("/your-order/pending");
  };
  return (
    <CartItemForm
      menu={menu}
      cartItem={cartItem}
      defaultFormData={generateDefaultFormData(item, cartItem)}
      item={item}
      isEditing
      onCompletion={onEditOrderItem}
      onDeletion={removeOrderItem}
    />
  );
};

export const NewCartItem = ({ menu }: { menu: Menu }) => {
  const { t } = useTranslation();
  const { itemUuid } = useParams();

  const navigate = useNavigate();
  const session = useSession();
  const dispatch = useSessionDispatch();

  const item = menu.sections
    .flatMap((s) => s.items)
    .find((i) => i.uuid == itemUuid);
  if (!item) throw new Error(t("items.menuItemNotFound"));

  const onAddOrderItemMobile = async (request: CartItemRequest) => {
    const cartItem = await createCartItem(request);
    dispatch({ action: "addCartItem", payload: cartItem });
    navigate("/your-order/pending");
  };
  const onAddOrderItemDesktop = async (request: CartItemRequest) => {
    const cartItem = await createCartItem(request);
    dispatch({ action: "addCartItem", payload: cartItem });
  };
  const fetchLiveItemCountdowns = useFetchLiveItemCountdown(item.uuid);

  // This feels like over kill for what is essentially an if statement based on the browser size.
  // However because the mobile app takes you to the review order page when the desktop page does not
  // This kinda ends up working pretty well, even if we do end up rendering the form twice
  return (
    <>
      <div className="md:hidden">
        <CartItemForm
          menu={menu}
          itemCount={fetchLiveItemCountdowns.data?.remainingCount}
          defaultFormData={generateDefaultFormData(item)}
          item={item}
          onCompletion={onAddOrderItemMobile}
        />
      </div>
      <div className="hidden md:block">
        <CartItemForm
          menu={menu}
          defaultFormData={generateDefaultFormData(item)}
          item={item}
          itemCount={fetchLiveItemCountdowns.data?.remainingCount}
          onCompletion={onAddOrderItemDesktop}
        />
      </div>
    </>
  );
};
const MultiplierIncrementer = ({
  multiplier,
  incrementMultiplier,
  decrementMultiplier,
  maxMultiplier,
}: {
  multiplier: number;
  incrementMultiplier: () => void;
  decrementMultiplier: () => void;
  maxMultiplier?: number;
}) => {
  return (
    <div className="w-full text-center p-[10px] flex items-center border-2 border-venue-primary rounded-venue-brand justify-around text-xl font-semibold ">
      <a
        className={classNames("cursor-pointer px-4 flex-1 ", {
          "opacity-30": multiplier <= 1,
        })}
        onClick={decrementMultiplier}
      >
        -
      </a>
      <div className="flex-1 text-center w-32">{multiplier}</div>
      {maxMultiplier == undefined ||
      (maxMultiplier && multiplier < maxMultiplier) ? (
        <a className="cursor-pointer flex-1 px-4" onClick={incrementMultiplier}>
          +
        </a>
      ) : (
        <a className="flex-1"></a>
      )}
    </div>
  );
};
